import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, SendHorizontal } from "lucide-react";
import NeuralCortex3D from "./NeuralCortex3D";
import { useBrand } from "../lib/brand";
import { api as http } from "../lib/api";

const LIME = "var(--brand)";
const TEAL = "#37E5D7";
const PINK = "#FF2BD6";

const OpTable = ({ rows }) => (
  <div className="mt-2 overflow-x-auto">
    <table className="w-full text-[12px]">
      <tbody>
        {rows.slice(0, 8).map((row, k) => (
          <tr key={k} className="border-t border-white/10">
            {Object.values(row).slice(0, 6).map((v, c) => <td key={c} className="p-1.5 text-white/75">{String(v)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const OpCard = ({ op, onConfirm, onOpen }) => (
  <div className="mt-2 border p-3 text-left" data-testid={`mari-op-${op.tool}`}
       style={{ borderColor: op.ok === false ? "#F8717155" : op.staged ? `${PINK}55` : "rgb(var(--brand-rgb)/0.2)", background: op.ok === false ? "#F871710d" : op.staged ? "#FF2BD60a" : "rgb(var(--brand-rgb)/0.04)" }}>
    <p className="font-mono text-[9px] tracking-[0.16em]" style={{ color: op.ok === false ? "#F87171" : op.staged ? PINK : TEAL }}>
      {op.tool?.toUpperCase()}{op.staged ? " · AWAITING YOUR CONFIRMATION" : ""}
    </p>
    {op.title && <p className="text-white text-sm font-semibold mt-1">{op.title}</p>}
    <p className="text-white/65 text-[13px] mt-1 whitespace-pre-wrap">{op.summary || op.error}</p>
    {op.staged && op.data?.args && Object.keys(op.data.args).length > 0 && (
      <pre className="mt-2 text-[11px] text-white/50">{JSON.stringify(op.data.args, null, 1)}</pre>
    )}
    {op.render === "table" && Array.isArray(op.data?.rows) && <OpTable rows={op.data.rows} />}
    {op.data?.path && op.render !== "navigate" && (
      <button onClick={() => onOpen(op.data.path)} className="mt-2 mr-2 font-mono text-[10px] tracking-[0.14em] text-white/60 hover:text-white" data-testid={`mari-open-${op.tool}`}>OPEN →</button>
    )}
    {op.staged && !op.done && (
      <button data-testid={`mari-confirm-${op.confirm_token}`} onClick={() => onConfirm(op)}
              className="mt-2 font-mono text-[10px] tracking-[0.14em] px-3 py-2 border transition-colors hover:bg-brand hover:text-black"
              style={{ borderColor: LIME, color: LIME }}>
        CONFIRM — RUN IT
      </button>
    )}
  </div>
);

const BARS = 40;
const Orb = ({ state, levelRef }) => {
  const core = useRef(null), halo = useRef(null), bars = useRef([]), ringA = useRef(null), ringB = useRef(null);
  useEffect(() => {
    let raf, spin = 0;
    const tick = (ms) => {
      const t = ms / 1000;
      const lv = levelRef.current || 0;
      const breathe = state === "idle" ? 0.04 * Math.sin(t * 1.6) : state === "thinking" ? 0.08 * Math.sin(t * 9) : 0;
      spin += (state === "thinking" ? 2.4 : 0.5 + lv * 6) * 0.016;
      if (core.current) {
        core.current.style.transform = `scale(${1 + breathe + lv * 0.9})`;
        core.current.style.boxShadow = `0 0 ${24 + lv * 90 + (state === "thinking" ? 20 : 0)}px ${PINK}, 0 0 ${8 + lv * 30}px #fff`;
      }
      if (halo.current) halo.current.style.opacity = String(0.15 + lv * 0.85);
      if (ringA.current) ringA.current.style.transform = `rotateX(70deg) rotateZ(${spin * 40}deg) scale(${1 + lv * 0.12})`;
      if (ringB.current) ringB.current.style.transform = `rotateY(65deg) rotateZ(${-spin * 55}deg) scale(${1 + lv * 0.18})`;
      bars.current.forEach((b, i) => {
        if (!b) return;
        const wave = 0.5 + 0.5 * Math.sin(t * 8 + i * 0.9) * Math.cos(t * 3.1 + i * 0.37);
        const h = 4 + (state === "speaking" ? lv * 34 * wave : state === "thinking" ? 6 * wave : 2 * wave);
        b.style.height = `${h}px`;
        b.style.opacity = String(0.35 + Math.min(0.65, lv + (state === "thinking" ? 0.3 : 0)));
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [state, levelRef]);
  return (
    <div className="relative w-36 h-36 mx-auto my-4" data-testid="mari-orb" data-state={state} style={{ perspective: 400 }}>
      <div ref={halo} className="absolute -inset-4 rounded-full" style={{ background: `radial-gradient(circle, ${PINK}55 0%, transparent 65%)`, opacity: 0.15 }} />
      {Array.from({ length: BARS }, (_, i) => (
        <span key={i} ref={(el) => { bars.current[i] = el; }} className="absolute left-1/2 top-1/2 w-[2px] rounded-full"
          style={{ background: i % 2 ? PINK : TEAL, transformOrigin: "50% 0", transform: `rotate(${(360 / BARS) * i}deg) translateY(46px)`, height: 4 }} />
      ))}
      <div ref={ringA} className="absolute inset-3 rounded-full" style={{ border: `1px solid ${PINK}66` }} />
      <div ref={ringB} className="absolute inset-5 rounded-full" style={{ border: `1px dashed ${TEAL}88` }} />
      <div ref={core} className="absolute inset-[38%] rounded-full" style={{ background: `radial-gradient(circle, #fff 0%, ${PINK} 45%, transparent 75%)` }} data-testid="mari-orb-core" />
    </div>
  );
};

const useHealth = (staged) => {
  const [h, setH] = useState(null);
  useEffect(() => {
    const load = async () => {
      try {
        const [q, a] = await Promise.all([http.get("/queue/stats"), http.get("/audit/verify")]);
        const score = Math.max(0, 100 - q.data.dead_letter * 8 - (a.data.valid ? 0 : 40) - (staged || 0) * 2 - q.data.pending * 2);
        const [label, color] = score >= 85 ? ["SYSTEM NOMINAL", LIME] : score >= 60 ? ["SYSTEM DEGRADED", "#FFB800"] : ["SYSTEM CRITICAL", "#F87171"];
        setH({ score, label, color, detail: `${q.data.dead_letter} dead-letter · ${q.data.pending} queued · audit chain ${a.data.valid ? "intact" : "BROKEN"} · ${a.data.entries} entries` });
      } catch (_) { /* health is decorative-only if endpoints fail */ }
    };
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [staged]);
  return h;
};

const CapabilityMap = ({ ctx }) => {
  const regions = ctx?.regions || [];
  return (
    <div className="border-t border-white/10 p-4" data-testid="mari-capability-map">
      <p className="font-mono text-[9px] tracking-[0.2em] text-white/40">SYNAPTOGENESIS · CAPABILITY MAP</p>
      <div className="mt-3 space-y-2">
        {regions.map((r) => {
          const v = Math.round((ctx?.scores?.[r] || 0) * 100);
          return (
            <div key={r} className="flex items-center gap-3 font-mono text-[10px]">
              <span className="w-24 text-white/55">{r}</span>
              <div className="flex-1 h-1.5 bg-white/5"><div className="h-full transition-[width] duration-700" style={{ width: `${Math.max(v, 2)}%`, background: `linear-gradient(90deg, ${TEAL}, ${PINK})` }} /></div>
              <span className="w-8 text-right text-white/70">{v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const useVoice = (base, levelRef) => {
  const [recording, setRecording] = useState(false);
  const actx = useRef(null);
  const rec = useRef(null);
  const chunks = useRef([]);
  const audio = useRef(null);
  const start = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
    const r = new MediaRecorder(stream, { mimeType: mime });
    chunks.current = [];
    r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
    r.start();
    rec.current = { r, stream, mime };
    setRecording(true);
  };
  const stop = () => new Promise((resolve) => {
    const { r, stream, mime } = rec.current;
    r.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      setRecording(false);
      const fd = new FormData();
      fd.append("file", new Blob(chunks.current, { type: mime }), mime.includes("mp4") ? "speech.mp4" : "speech.webm");
      try {
        const d = await (await fetch(`${base}/voice/transcribe`, { method: "POST", body: fd })).json();
        resolve(d.text || "");
      } catch (_) { resolve(""); }
    };
    r.stop();
  });
  const speak = async (text) => {
    const r = await fetch(`${base}/voice/speak`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
    if (!r.ok) return;
    audio.current?.pause();
    const a = new Audio(URL.createObjectURL(await r.blob()));
    audio.current = a;
    let raf;
    try {
      actx.current = actx.current || new (window.AudioContext || window.webkitAudioContext)();
      await actx.current.resume();
      const an = actx.current.createAnalyser();
      an.fftSize = 512;
      actx.current.createMediaElementSource(a).connect(an);
      an.connect(actx.current.destination);
      const buf = new Uint8Array(an.fftSize);
      const loop = () => {
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        const rms = Math.min(1, Math.sqrt(sum / buf.length) * 4);
        levelRef.current = levelRef.current * 0.6 + rms * 0.4;
        raf = requestAnimationFrame(loop);
      };
      loop();
    } catch (_) { /* analyser optional; orb falls back to synthetic level */ }
    await new Promise((res) => { a.onended = res; a.onerror = res; a.play().catch(res); });
    cancelAnimationFrame(raf);
    levelRef.current = 0;
  };
  const hush = () => audio.current?.pause();
  return { recording, start, stop, speak, hush };
};

export default function MariConsole({
  baseUrl = "", token = null, sessionId = "mari-main", title = "MARI", subtitle = "Operations brain",
  placeholder = "Speak or type to MARI…", height = 520,
}) {
  const api = `${baseUrl}/api/mari`;
  const navigate = useNavigate();
  const { setBrand } = useBrand();
  const [turns, setTurns] = useState([]);
  const [text, setText] = useState(() => new URLSearchParams(window.location.search).get("ask") || "");
  const [state, setState] = useState("idle");
  const [ctx, setCtx] = useState(null);
  const [memory, setMemory] = useState([]);
  const endRef = useRef(null);
  const health = useHealth(ctx?.staged_open);
  const levelRef = useRef(0);
  const voice = useVoice(api, levelRef);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("mari-voice") !== "off");
  const [transcribing, setTranscribing] = useState(false);
  const toggleVoice = () => { const v = !voiceOn; setVoiceOn(v); localStorage.setItem("mari-voice", v ? "on" : "off"); if (!v) voice.hush(); };

  const headers = useCallback(() => ({
    "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), [token]);

  const refresh = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([
        fetch(`${api}/context?session_id=${encodeURIComponent(sessionId)}`, { headers: headers() }),
        fetch(`${api}/memory`, { headers: headers() }),
      ]);
      if (c.ok) setCtx(await c.json());
      if (m.ok) setMemory((await m.json()).memory || []);
    } catch (_) { /* console still works without telemetry */ }
  }, [api, headers, sessionId]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${api}/history?session_id=${encodeURIComponent(sessionId)}`, { headers: headers() });
        if (r.ok) setTurns(((await r.json()).messages || []).map((m) => ({ role: m.role, text: m.text, ops: [] })));
      } catch (_) { /* first run */ }
      refresh();
    })();
  }, [api, headers, sessionId, refresh]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [turns, state]);

  const go = (p) => navigate(p.startsWith("/app") ? p : `/app${p === "/" ? "" : p}`);
  const applySideEffects = (ops) => {
    ops.forEach((op) => {
      if (op.render === "navigate" && op.data?.path) setTimeout(() => go(op.data.path), 900);
      if (op.data?.reload_brand) http.get("/settings").then((r) => setBrand(r.data));
    });
  };

  const send = async (message) => {
    const msg = (message ?? text).trim();
    if (!msg || state !== "idle") return;
    setText("");
    setTurns((t) => [...t, { role: "user", text: msg, ops: [] }]);
    setState("thinking");
    try {
      const r = await fetch(`${api}/converse`, { method: "POST", headers: headers(), body: JSON.stringify({ message: msg, session_id: sessionId }) });
      const d = await r.json();
      setState("speaking");
      setTurns((t) => [...t, { role: "mari", text: d.reply || "…", ops: d.ops || [] }]);
      applySideEffects(d.ops || []);
      refresh();
      if (voiceOn && d.reply) {
        await voice.speak(d.reply).catch(() => {});
        setState("idle");
      } else {
        const t0 = performance.now();
        const fake = () => { const e = performance.now() - t0; if (e > 1400) { levelRef.current = 0; setState("idle"); return; }
          levelRef.current = 0.35 + 0.3 * Math.abs(Math.sin(e / 70)) * Math.random(); requestAnimationFrame(fake); };
        requestAnimationFrame(fake);
      }
    } catch (e) {
      setState("idle");
      setTurns((t) => [...t, { role: "mari", text: "My uplink dropped. Say it again?", ops: [] }]);
    }
  };

  const confirmOp = async (op) => {
    setState("thinking");
    try {
      const r = await fetch(`${api}/confirm`, { method: "POST", headers: headers(), body: JSON.stringify({ token: op.confirm_token }) });
      const d = await r.json();
      op.done = true;
      setTurns((t) => [...t, { role: "mari", text: d.spoken || d.detail || "Done.", ops: d.ok ? [{ ...d, staged: false }] : [] }]);
      if (d.ok) applySideEffects([d]);
      refresh();
      if (voiceOn && d.spoken) await voice.speak(d.spoken).catch(() => {});
    } finally { setState("idle"); }
  };

  const toggleMic = async () => {
    if (voice.recording) {
      setTranscribing(true);
      const heard = await voice.stop();
      setTranscribing(false);
      if (heard) send(heard);
      return;
    }
    if (state !== "idle") return;
    voice.hush();
    try { await voice.start(); } catch (_) { setTurns((t) => [...t, { role: "mari", text: "I can't reach your microphone — check the browser permission.", ops: [] }]); }
  };

  const QUICK = [["STATUS REPORT", "Give me a status report on the defense grid"], ["FIGHT QUEUE", "Which open disputes should I fight first and why?"],
    ["EVIDENCE INTEGRITY", "Verify the audit chain and check the ingestion queue"], ["WHO ARE YOU", "Who are you and what can you control?"]];
  const caps = (ctx?.regions || []).slice(0, 6).map((r) => [r, Math.round((ctx?.scores?.[r] || 0) * 100)]);

  return (
    <div className="grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] border border-white/10 bg-canvas -m-4 lg:-m-8" data-testid="mari-console" style={{ minHeight: "calc(100vh - 64px)" }}>
      <div className="flex flex-col border-r border-white/10 p-5 lg:p-6 min-w-0" style={{ height: "calc(100vh - 64px)" }}>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-extrabold text-white tracking-tight">{title} · DIRECT LINK</h1>
          <span className="font-mono text-[9px] tracking-[0.18em] px-2 py-1 border" style={{ borderColor: `${PINK}88`, color: PINK }} data-testid="mari-link-chip">
            {voice.recording ? "LISTENING — TAP MIC TO SEND" : transcribing ? "TRANSCRIBING…" : state === "idle" ? "LINK OPEN — LISTENING FOR YOU" : state === "thinking" ? "SYNAPSES FIRING…" : "SHE SPEAKS"}
          </span>
          <button onClick={toggleVoice} data-testid="mari-voice-toggle" className="font-mono text-[9px] tracking-[0.18em] px-2 py-1 border transition-colors"
            style={{ borderColor: voiceOn ? PINK : "#ffffff30", color: voiceOn ? PINK : "#ffffff60" }}>
            {voiceOn ? "VOICE ON" : "VOICE OFF"}
          </button>
        </div>
        <p className="font-mono text-[10px] tracking-[0.2em] mt-1" style={{ color: LIME }}>{subtitle.toUpperCase()}</p>
        {health && (
          <div className="mt-4 flex items-center gap-2 px-3 py-2 border font-mono text-[10px] tracking-[0.12em]" style={{ borderColor: `color-mix(in srgb, ${health.color} 33%, transparent)`, color: health.color, background: `color-mix(in srgb, ${health.color} 5%, transparent)` }} data-testid="mari-health-banner">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: health.color }} /> {health.label} · HEALTH {health.score} · {health.detail}
          </div>
        )}
        <Orb state={state} levelRef={levelRef} />
        <div className="flex flex-wrap justify-center gap-1.5">
          {caps.map(([k, v]) => (
            <span key={k} className="font-mono text-[9px] tracking-[0.1em] px-2 py-1 border border-white/15 text-white/70" data-testid={`mari-cap-${k}`}>{k} {v}</span>
          ))}
          <span className="font-mono text-[9px] tracking-[0.1em] px-2 py-1 border" style={{ borderColor: LIME, color: LIME }}>MEMORY {ctx?.memory_count ?? 0}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          {QUICK.map(([l, m], i) => (
            <button key={l} onClick={() => send(m)} disabled={state !== "idle"} data-testid={`mari-quick-${i}`}
              className="font-mono text-[9.5px] tracking-[0.14em] py-2.5 border border-white/10 bg-white/[0.02] text-white/70 hover:border-brand hover:text-brand transition-colors disabled:opacity-40">{l}</button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto space-y-4 mt-4 border border-white/10 bg-white/[0.015] p-4 min-h-0" data-testid="mari-transcript">
          {turns.length === 0 && (
            <p className="text-white/45 text-sm" data-testid="mari-empty">
              Direct link open, operator. I'm MARI — I run every module of this platform: disputes, evidence, risk, consent, issuers, ingestion and the audit trail. Anything that writes waits for your confirmation.
            </p>
          )}
          {turns.map((t, i) => (
            <div key={i} data-testid={`mari-turn-${i}`} className={t.role === "user" ? "text-right" : ""}>
              <span className="font-mono text-[9px] tracking-[0.18em]" style={{ color: t.role === "user" ? "#ffffff55" : LIME }}>{t.role === "user" ? "YOU" : title.toUpperCase()}</span>
              <p className={`text-[14px] leading-relaxed mt-1 ${t.role === "user" ? "text-white/80" : "text-white"}`}>{t.text}</p>
              {(t.ops || []).map((op, j) => <OpCard key={j} op={op} onConfirm={confirmOp} onOpen={go} />)}
            </div>
          ))}
          {state === "thinking" && <p className="font-mono text-[10px] tracking-[0.2em] animate-pulse" style={{ color: TEAL }} data-testid="mari-thinking">…synapses firing</p>}
          <div ref={endRef} />
        </div>
        {memory.length > 0 && (
          <div className="mt-3 flex gap-2 overflow-x-auto" data-testid="mari-memory">
            {memory.slice(0, 4).map((m) => (
              <span key={m.id} className="shrink-0 max-w-[260px] truncate text-[11px] text-white/60 border border-white/10 px-2 py-1">
                <span className="font-mono text-[9px] mr-1.5" style={{ color: TEAL }}>{(m.kind || "note").toUpperCase()}</span>{m.text}
              </span>
            ))}
          </div>
        )}
        <div className="mt-3 flex gap-2">
          <button onClick={toggleMic} disabled={transcribing || (state !== "idle" && !voice.recording)} data-testid="mari-mic-btn" title="Tap to talk, tap again to send"
            className={`w-11 flex items-center justify-center border transition-colors disabled:opacity-40 ${voice.recording ? "animate-pulse" : "hover:text-white"}`}
            style={{ borderColor: voice.recording ? PINK : "#ffffff26", color: voice.recording ? PINK : "#ffffff80", boxShadow: voice.recording ? `0 0 18px ${PINK}88` : "none" }}>
            <Mic className="w-4 h-4" />
          </button>
          <input data-testid="mari-input" value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && send()}
                 className="flex-1 bg-canvas border px-3 py-3 text-[14px] text-white outline-none focus:border-brand transition-colors" style={{ borderColor: "#ffffff24" }} />
          <button data-testid="mari-send" onClick={() => send()} disabled={state !== "idle"}
                  className="w-12 flex items-center justify-center border transition-colors hover:bg-brand hover:text-black disabled:opacity-40" style={{ borderColor: LIME, color: LIME }}>
            <SendHorizontal className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="flex flex-col min-w-0">
        <NeuralCortex3D p={{ id: "mari", stages: (ctx?.tools || []).map((t) => ({ title: t.toUpperCase() })) }}
          status={null} linkState={state === "idle" ? "online" : state} height={height} chrome health={health}
          contextUrl={`${api}/context?session_id=${encodeURIComponent(sessionId)}`} authToken={token} levelRef={levelRef} />
        <div className="grid grid-cols-4 border-t border-white/10">
          {[["EXCHANGES", ctx?.turns ?? 0, PINK], ["AGENT OPS", ctx?.ops_total ?? 0, TEAL], ["REMEMBERS", ctx?.memory_count ?? 0, "#FFE600"], ["AWAITING YOU", ctx?.staged_open ?? 0, LIME]].map(([k, v, c]) => (
            <div key={k} className="py-4 text-center border-r border-white/10 last:border-r-0" data-testid={`mari-stat-${k.replace(" ", "-")}`}>
              <div className="text-2xl font-extrabold font-mono" style={{ color: c }}>{v}</div>
              <div className="font-mono text-[8.5px] tracking-[0.2em] text-white/40 mt-1">{k}</div>
            </div>
          ))}
        </div>
        <CapabilityMap ctx={ctx} />
      </div>
    </div>
  );
}
